import { Spacing } from '@opallabs/gem'

/**
 * The single source of truth for the grid's vertical rhythm.
 *
 * The per-day render budget divides the free height of a week row by the height
 * of one chip, so the numbers the layout reasons about and the numbers the
 * browser paints must be the same. `styles.ts` publishes these as CSS custom
 * properties instead of the layout measuring the DOM, which keeps the budget a
 * pure function and keeps the first paint free of a measure-then-reflow pass.
 *
 * Several of these have no Gem token: the design puts a day cell on a tighter
 * grid than the standard spacing scale covers.
 */
export const CalendarSizes = {
  /**
   * Space under a cell's contents. The design hangs the day header off the top
   * edge, so there is no matching space above it.
   */
  cellPaddingBlockEnd: 3,
  /** Gap between stacked chips, and between the day header and what follows it. */
  chipGap: Spacing.XS / 2,
  /** Height of one content chip at row density. */
  chipBlockSize: Spacing.XS * 5,
  /**
   * Height of one content card at card density. Fixed, whatever the card holds:
   * a compact card draws no picture, so nothing in the data can change it.
   *
   * It has to be at least the lines, the gaps between them, the padding and
   * the border, or the last line paints over the card's bottom edge. See the
   * arithmetic in `sizes.tests.ts`.
   */
  cardBlockSize: 64,
  /** Height of a content card that draws no moment line. */
  cardBlockSizeWithoutMoment: 48,
  /** Height of one line of text on a card: the account line, and each title line. */
  cardLineBlockSize: 12,
  /**
   * Lines a card keeps for its title, whether or not the title fills them.
   *
   * The room is always held, so the height of a card is a question of the
   * settings and never of the words a person typed. A budget that had to
   * measure the title could only be worked out after the browser had drawn it.
   */
  cardTitleLines: 2,
  /** Height of a card's line that names the moment it belongs to. */
  cardMomentLineBlockSize: 12,
  /**
   * Channel mark on a card's detail lines. The design's frames measure 14px,
   * but they carry padding of their own; the channel artwork fills its box
   * edge to edge, so it takes the glyph's 12px.
   */
  cardDetailIconSize: 12,
  /**
   * Moment mark on a card's moment line. The gem glyph paints its mark at
   * three quarters of its box, so this box puts the same 12px of ink on the
   * line as the channel mark beside it.
   */
  cardMomentIconSize: 16,
  /**
   * Channel mark on a chip at row density. As above: the design's 14px frame
   * holds a 12px glyph.
   */
  chipIconSize: 12,
  /** Gap between a card's lines. */
  cardRowGap: 4,
  /**
   * Space between a card's border and its lines. The design's 4px inset
   * includes the 1px border.
   */
  cardPaddingBlock: 3,
  /**
   * Extra space over the first chip of a run, on top of `chipGap`. It is the
   * only thing that says where one run of a sorted day ends and the next
   * begins, so it has to read as more than the space between two chips.
   */
  runGap: Spacing.XS,
  /** Height of the "+N more" row. */
  overflowBlockSize: 18,
  /** Height of the day-number header. `chipGap` separates it from the column. */
  dayHeaderBlockSize: 26,
  /**
   * Height of the day-name row. The day headers pin under it while a week is on
   * screen, so the offset they are held at and the height the browser paints
   * have to be the same number: `styles.ts` sets the row to this height.
   */
  weekdayRowBlockSize: 24,
  /** Diameter of the day number, which is a circle on today. */
  dayNumberBlockSize: Spacing.XS * 5,
  /** Space a chip or a bar is held in from the leading and trailing cell edges. */
  itemInsetInline: 3,
  /** Gap between bar lanes. */
  laneGap: Spacing.XS / 2,
  /** Extra space between the last bar lane and the first chip under it. */
  laneStackGap: Spacing.XS / 2,
  /** Height of one multi-day bar. */
  barBlockSize: 20,
  /** Kind and privacy marks on a moment bar. */
  barIconSize: 14,
  /**
   * The lock beside Open in the item panel. The design gives it a 20px frame,
   * larger than the 16px marks on the rows, because it stands alone against
   * the title rather than in a cluster.
   */
  panelLockSize: 20,
  /** Default height of a week row. Overridable through `CalendarDisplay`. */
  weekRowBlockSize: Spacing.XS * 37,
} as const

/**
 * How tall a content card is for the settings it is drawn under.
 *
 * With the moment badge off, no card can draw a moment line, so every card
 * loses that line and the gap over it instead of holding an empty row. Which
 * height applies is a question of the settings and never of the data: a card
 * whose content belongs to no moment keeps the height of its neighbours.
 */
export const cardBlockSize = ({
  shouldShowMomentBadge = true,
}: {
  shouldShowMomentBadge?: boolean
}): number =>
  shouldShowMomentBadge
    ? CalendarSizes.cardBlockSize
    : CalendarSizes.cardBlockSizeWithoutMoment
